import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const WhyChooseMe = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  const trustFactors = [
    {
      icon: "💰",
      title: "Transparent Pricing",
      description: "No hidden costs",
      color: "from-green-400 to-emerald-600"
    },
    {
      icon: "💬",
      title: "Friendly Communication",
      description: "Quick responses",
      color: "from-blue-400 to-cyan-600"
    },
    {
      icon: "🛠️",
      title: "Post-launch Support",
      description: "30 days free",
      color: "from-purple-400 to-violet-600"
    },
    {
      icon: "📱",
      title: "Mobile Responsive",
      description: "All devices",
      color: "from-pink-400 to-rose-600"
    },
    {
      icon: "🌐",
      title: "Own Your Domain",
      description: "Affordable prices",
      color: "from-indigo-400 to-blue-600"
    },
    {
      icon: "🎁",
      title: "FREE Hosting 1 Year",
      description: "Most highlighted offer",
      color: "from-emerald-400 to-green-600",
      highlight: true
    },
    {
      icon: "💸",
      title: "Hosting ₹500/Year",
      description: "Lowest in market",
      color: "from-yellow-400 to-orange-600",
      highlight: true
    },
    {
      icon: "✅",
      title: `${projectCount}+ Projects`,
      description: "100% satisfaction",
      color: "from-orange-400 to-red-600"
    }
  ];

  // Animated counter for projects
  useEffect(() => {
    const timer = setInterval(() => {
      setProjectCount(prev => prev < 10 ? prev + 1 : 10);
    }, 100);
    return () => clearInterval(timer);
  }, []);

  // Auto-rotate active factor
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % trustFactors.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [trustFactors.length]);

  return (
    <section className="mobile-why px-4 relative overflow-hidden sm:py-12 md:py-14">
      {/* Compact Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-purple-50/20 to-blue-50/30 dark:from-gray-800/30 dark:via-purple-900/10 dark:to-gray-800/30"></div>
      
      <div className="max-w-6xl mx-auto relative">
        {/* Compact Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6 sm:mb-8"
        >
          <h2 className="text-3xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Why Choose Us?
            </span>
          </h2>
          <p className="text-sm sm:text-sm text-gray-600 dark:text-gray-300">
            Trusted by clients worldwide
          </p>
        </motion.div>

        {/* Responsive Layouts */}
        <div className="relative">
          {/* Desktop: 4x2 Grid with Highlighted Hosting Offers */}
          <div className="hidden lg:block">
            {/* Top Row - Highlighted Hosting Offers */}
            <div className="mb-12">
              <div className="flex justify-between items-center max-w-5xl mx-auto gap-8">
                
                {/* Left: FREE Hosting Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="flex-1"
                >
                  <motion.div 
                    className="bg-gradient-to-br from-emerald-500 to-green-600 text-white p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden"
                    animate={{
                      boxShadow: [
                        "0 25px 50px -12px rgba(16, 185, 129, 0.5)",
                        "0 25px 50px -12px rgba(16, 185, 129, 1.0)",
                        "0 25px 50px -12px rgba(16, 185, 129, 0.5)"
                      ]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {/* Subtle glow overlay */}
                    <motion.div
                      className="absolute inset-0 bg-white/10 rounded-3xl"
                      animate={{
                        opacity: [0.1, 0.4, 0.1]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <div className="text-center relative z-10">
                      <div className="text-5xl mb-4">🎁</div>
                      <h3 className="text-2xl font-bold mb-3">FREE Hosting</h3>
                      <h4 className="text-xl font-semibold mb-2">1 Year</h4>
                      <p className="text-base opacity-90">Most highlighted offer!</p>
                      <div className="mt-4 bg-white/20 rounded-full px-4 py-2 inline-block">
                        <span className="text-sm font-bold">Save ₹6000+</span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Center: Projects Stats */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="flex-shrink-0"
                >
                  <div className="w-48 h-48 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 border-4 border-blue-200 dark:border-blue-700 flex flex-col items-center justify-center shadow-xl">
                    <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {projectCount}+
                    </div>
                    <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 text-center mt-2">
                      Projects<br />Completed
                    </div>
                  </div>
                </motion.div>

                {/* Right: ₹500/Year Hosting Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="flex-1"
                >
                  <motion.div 
                    className="bg-gradient-to-br from-orange-500 to-red-600 text-white p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden"
                    animate={{
                      boxShadow: [
                        "0 25px 50px -12px rgba(249, 115, 22, 0.5)",
                        "0 25px 50px -12px rgba(249, 115, 22, 1.0)",
                        "0 25px 50px -12px rgba(249, 115, 22, 0.5)"
                      ]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {/* Subtle glow overlay */}
                    <motion.div
                      className="absolute inset-0 bg-white/10 rounded-3xl"
                      animate={{
                        opacity: [0.1, 0.4, 0.1]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <div className="text-center relative z-10">
                      <div className="text-5xl mb-4">💸</div>
                      <h3 className="text-2xl font-bold mb-3">Renews at just</h3>
                      <h4 className="text-xl font-semibold mb-2">₹500/Year</h4>
                      <p className="text-base opacity-90">Lowest in market!</p>
                      <div className="mt-4 bg-white/20 rounded-full px-4 py-2 inline-block">
                        <span className="text-sm font-bold">Unbeatable Price</span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

              </div>
            </div>

            {/* Bottom Row - Regular Trust Factors */}
            <div className="grid grid-cols-5 gap-4">
              {trustFactors.filter(factor => !factor.highlight && !factor.title.includes('Projects')).map((factor, index) => (
                <motion.div
                  key={`regular-${index}`}
                  className="p-4 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-lg text-center hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (index + 3) * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`w-12 h-12 mx-auto rounded-lg bg-gradient-to-br ${factor.color} flex items-center justify-center text-white text-lg mb-3`}>
                    {factor.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                    {factor.title}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {factor.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Tablet: 2x4 Grid with Top Highlights */}
          <div className="hidden md:block lg:hidden">
            <div className="max-w-4xl mx-auto">
              {/* Top Row - Highlighted Offers */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {trustFactors.filter(factor => factor.highlight).map((factor, index) => (
                  <motion.div
                    key={`tablet-highlight-${index}`}
                    className={`p-5 rounded-xl bg-gradient-to-br ${factor.color} text-white shadow-xl`}
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{factor.icon}</div>
                      <div>
                        <h3 className="font-bold text-base mb-1">{factor.title}</h3>
                        <p className="text-sm opacity-90">{factor.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Stats Bar */}
              <motion.div
                className="flex justify-center mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-6 p-4 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {projectCount}+
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Projects</div>
                  </div>
                  <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">100%</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Satisfaction</div>
                  </div>
                </div>
              </motion.div>

              {/* Regular Factors Grid */}
              <div className="grid grid-cols-3 gap-4">
                {trustFactors.filter(factor => !factor.highlight && !factor.title.includes('Projects')).map((factor, index) => (
                  <motion.div
                    key={`tablet-regular-${index}`}
                    className="p-4 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-lg text-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: (index + 2) * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className={`w-10 h-10 mx-auto rounded-lg bg-gradient-to-br ${factor.color} flex items-center justify-center text-white text-base mb-2`}>
                      {factor.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                      {factor.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {factor.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile: Vertical Stack with Highlights First */}
          <div className="block md:hidden">
            {/* Highlighted Offers */}
            <div className="space-y-4 mb-6">
              {/* FREE Hosting Card */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileTap={{ scale: 0.95 }}
                className="p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl">🎁</div>
                  <div>
                    <h3 className="font-bold text-base mb-1">FREE Hosting 1 Year</h3>
                    <p className="text-sm opacity-90">Most highlighted offer!</p>
                  </div>
                </div>
              </motion.div>

              {/* ₹500/Year Hosting Card */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileTap={{ scale: 0.95 }}
                className="p-4 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="text-2xl">💸</div>
                  <div className="text-center">
                    <h3 className="font-bold text-base mb-1">Renews at just ₹500/Year</h3>
                    <p className="text-sm opacity-90">Lowest in market!</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Regular Factors Grid */}
            <div className="grid grid-cols-2 gap-3">
              {trustFactors.filter(factor => !factor.highlight).map((factor, index) => (
                <motion.div
                  key={`mobile-regular-${index}`}
                  className="p-3 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-md text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (index + 2) * 0.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="text-center h-full flex flex-col justify-center">
                    <div className="text-lg mb-1">{factor.icon}</div>
                    <div className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5 leading-tight">
                      {factor.title}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 leading-tight">
                      {factor.description}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Compact Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-6 sm:mt-8 flex flex-wrap justify-center gap-2 sm:gap-4 text-center"
        >
          {/* Highlighted Hosting Offers */}
          {/* <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg animate-pulse">
            <span className="text-sm sm:text-sm font-bold">🎁 FREE Hosting 1 Year</span>
          </div>
          <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg">
            <span className="text-sm sm:text-sm font-bold">💸 Renewal ₹500/Year</span>
          </div> */}
          {/* <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg">
            <span className="text-sm sm:text-sm font-bold">🌐 Own Domain</span>
          </div> */}
          
          {/* Original Stats */}
          <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <span className="text-sm sm:text-sm font-semibold text-blue-600 dark:text-blue-400">💯 Satisfaction</span>
          </div>
          <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <span className="text-sm sm:text-sm font-semibold text-purple-600 dark:text-purple-400">⚡ Fast Delivery</span>
          </div>
          <div className="px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <span className="text-sm sm:text-sm font-semibold text-green-600 dark:text-green-400">🔒 Secure Code</span>
          </div>
        </motion.div>

        {/* Compact CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-4 sm:mt-6"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/contact')}
            className="inline-flex items-center px-6 py-4 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm sm:text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            Drop a Message
            <svg className="ml-1 w-3 h-3 sm:ml-2 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.button>
        </motion.div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default WhyChooseMe;
